import axios, { AxiosInstance } from 'axios';

// In-memory token storage for admin auth (separate from user auth)
class AdminTokenStore {
  private adminToken: string | null = null;

  setToken(token: string): void {
    this.adminToken = token;
  }

  getToken(): string | null {
    return this.adminToken;
  }

  clear(): void {
    this.adminToken = null;
  }
}

export const adminTokenStore = new AdminTokenStore();

// Create axios instance for admin API
const adminApiUrl = process.env.REACT_APP_API_URL;
if (!adminApiUrl) {
  throw new Error('REACT_APP_API_URL environment variable is required');
}
const adminApi: AxiosInstance = axios.create({
  baseURL: `${adminApiUrl}/admin`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add admin auth token
adminApi.interceptors.request.use(
  (config) => {
    const token = adminTokenStore.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
adminApi.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If 401 (Unauthorized), clear token and redirect to admin login
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      adminTokenStore.clear();
      // Redirect to admin login
      window.location.href = '/admin/login';
    }

    return Promise.reject(error);
  }
);

// Admin API endpoints (mapped to Express backend routes)
export const adminAuthAPI = {
  login: (credentials: { email: string; password: string }) =>
    adminApi.post('/login', credentials),

  initializeSuperAdmin: (data: {
    email: string;
    password: string;
    name: string;
  }) => adminApi.post('/initialize', data),
};

export const adminProfileAPI = {
  getProfile: () =>
    adminApi.get('/profile'),

  getDashboardStats: () =>
    adminApi.get('/analytics/dashboard'),
};

export const adminAnalyticsAPI = {
  getTransactionVolume: () =>
    adminApi.get('/analytics/transaction-volume'),

  getUserGrowth: () =>
    adminApi.get('/analytics/user-growth'),

  getRecentTransactions: (limit: number = 5) =>
    adminApi.get('/analytics/recent-transactions', { params: { limit } }),

  getEarningsAnalytics: () =>
    adminApi.get('/analytics/earnings'),
};

export const adminUsersAPI = {
  getAllUsers: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }) => adminApi.get('/users', { params }),

  getUserById: (id: string) =>
    adminApi.get(`/users/${id}`),

  updateUserStatus: (id: string, status: string) =>
    adminApi.put(`/users/${id}/status`, { status }),
};

export const adminMerchantsAPI = {
  getMerchants: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }) => adminApi.get('/merchants', { params }),

  getMerchantById: (id: string) =>
    adminApi.get(`/merchants/${id}`),

  reviewMerchant: (id: string, status: string, rejectionReason?: string) =>
    adminApi.put(`/merchants/${id}/review`, { status, rejectionReason }),

  reviewMerchantDocument: (docId: string, status: string, rejectionReason?: string) =>
    adminApi.put(`/merchants/documents/${docId}/review`, { status, rejectionReason }),

  suspendMerchant: (id: string, reason: string) =>
    adminApi.put(`/merchants/${id}/suspend`, { reason }),
};

export const adminWalletAPI = {
  getAdminWallet: () =>
    adminApi.get('/wallet'),

  getAdminEarnings: () =>
    adminApi.get('/earnings'),

  getSystemEarnings: () =>
    adminApi.get('/system-earnings'),
};

export const adminHealthAPI = {
  getHealthErrors: () =>
    adminApi.get('/health/errors'),
};

export default adminApi;
